import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { EyeIcon } from '@heroicons/react/24/outline';
import { getEmployeePerformanceFileBlobApi } from '../../services/axios/AuthApis/bgvApis';
import toast from "../../utils/notify";
import FilePreviewModal from './FilePreviewModal';
import { DataTable } from '../common/DataTable';

const PerformanceView = ({
  performanceLoading,
  performanceRecords,
  performancePagination,
  setPerformancePagination,
}) => {
  // { file: Blob, title } while a performance document is open.
  const [preview, setPreview] = useState(null);
  // No filter state exists upstream for this view - kept local, same
  // client-side-on-current-page approach as BGV's Employee/Department/DOJ.
  const [filters, setFilters] = useState({ employee: '', department: '', doj: '', reportingTo: '' });

  const handleViewPerformance = async (employeeId) => {
    if (!employeeId) {
      toast.error('Employee ID is required to view performance record');
      return;
    }
    try {
      // The newest performance file, streamed through the backend and shown from memory.
      setPreview({ file: await getEmployeePerformanceFileBlobApi(employeeId), title: 'Performance Document' });
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.info('No performance file uploaded for this employee');
      } else {
        console.error('Error fetching performance record:', err);
        toast.error('Could not open the performance document');
      }
    }
  };

  const displayedRecords = performanceRecords.filter((r) => {
    if (filters.employee && !(r.firstname || '').toLowerCase().includes(filters.employee.toLowerCase())) return false;
    if (filters.department && !(r.department || '').toLowerCase().includes(filters.department.toLowerCase())) return false;
    if (filters.doj && !String(r.DOJ || '').toLowerCase().includes(filters.doj.toLowerCase())) return false;
    if (filters.reportingTo && !(r.reporting_to || '').toLowerCase().includes(filters.reportingTo.toLowerCase())) return false;
    return true;
  });

  const columns = [
    {
      id: 'employee',
      accessorFn: (r) => r.firstname,
      header: 'Employee',
      enableSorting: false,
      meta: { filter: { variant: 'text', label: 'Employee', placeholder: 'Search employee' } },
      cell: ({ row }) => <span className="text-gray-900">{row.original.firstname || '—'}</span>,
    },
    {
      id: 'department',
      accessorFn: (r) => r.department,
      header: 'Department',
      enableSorting: false,
      meta: { filter: { variant: 'text', label: 'Department', placeholder: 'Search department' } },
      cell: ({ row }) => <span className="text-gray-900">{row.original.department || '—'}</span>,
    },
    {
      id: 'doj',
      accessorFn: (r) => r.DOJ,
      header: 'DOJ',
      enableSorting: false,
      meta: { filter: { variant: 'text', label: 'DOJ', placeholder: 'Search DOJ' } },
      cell: ({ row }) => <span className="text-gray-900">{row.original.DOJ || '—'}</span>,
    },
    {
      id: 'status',
      accessorFn: (r) => r.status,
      header: 'Performance Status',
      enableSorting: false,
      meta: {
        filter: {
          variant: 'select',
          label: 'Status',
          placeholder: 'All statuses',
          options: [
            { value: 'Good', label: 'Good' },
            { value: 'Average', label: 'Average' },
            { value: 'Poor', label: 'Poor' },
          ],
        },
      },
      cell: ({ row }) => <span className="text-gray-900">{row.original.status || 'Good'}</span>,
    },
    {
      id: 'reporting_to',
      accessorFn: (r) => r.reporting_to,
      header: 'Reporting To',
      enableSorting: false,
      meta: { filter: { variant: 'text', label: 'Reporting To', placeholder: 'Search reporting to' } },
      cell: ({ row }) => <span className="text-gray-900">{row.original.reporting_to || '—'}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => handleViewPerformance(row.original.employee_id)}
          className="text-blue-600 hover:text-blue-900"
          title="View uploaded file"
        >
          <EyeIcon className="w-4 h-4" />
        </button>
      ),
    },
  ];

  const columnFilters = [
    ...(filters.employee ? [{ id: 'employee', value: filters.employee }] : []),
    ...(filters.department ? [{ id: 'department', value: filters.department }] : []),
    ...(filters.doj ? [{ id: 'doj', value: filters.doj }] : []),
    ...(filters.status ? [{ id: 'status', value: filters.status }] : []),
    ...(filters.reportingTo ? [{ id: 'reporting_to', value: filters.reportingTo }] : []),
  ];

  return (
  <motion.div
    key="performance-records-table"
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    transition={{ duration: 0.3 }}
  >
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Performance Management</h3>
        <p className="text-sm text-gray-600 mt-1">Track employee KPI/KRI, goals, and ratings</p>
      </div>

      <DataTable
        columns={columns}
        data={displayedRecords}
        getRowId={(r) => String(r.employee_id)}
        loading={performanceLoading}
        emptyMessage="No performance records found"
        manualPagination
        rowCount={performancePagination.count || 0}
        pagination={{ pageIndex: (performancePagination.page || 1) - 1, pageSize: performancePagination.page_size }}
        onPaginationChange={(updater) => {
          const currentPagination = { pageIndex: (performancePagination.page || 1) - 1, pageSize: performancePagination.page_size };
          const nextPagination = typeof updater === 'function' ? updater(currentPagination) : updater;
          setPerformancePagination((p) => ({ ...p, page: nextPagination.pageIndex + 1, page_size: nextPagination.pageSize }));
        }}
        enableColumnVisibility={false}
        enableColumnResizing
        enableColumnFilters
        manualFiltering
        columnFilters={columnFilters}
        onColumnFiltersChange={(updater) => {
          const nextFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
          const values = Object.fromEntries(nextFilters.map(({ id, value }) => [id, value]));
          setFilters({
            employee: values.employee || '',
            department: values.department || '',
            doj: values.doj || '',
            status: values.status || '',
            reportingTo: values.reporting_to || '',
          });
        }}
        className="overflow-hidden"
        maxBodyHeight="65vh"
      />
    </div>
    <FilePreviewModal file={preview?.file} title={preview?.title} onClose={() => setPreview(null)} />
  </motion.div>
  );
};

export default PerformanceView;
